import { useEffect, useState } from "react";
import api, { money, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend,
} from "recharts";

const LEVEL_LABELS = ["0%", "1%", "2%", "3%", "4%", "5%"];

function Row({ label, value, bold, accent }) {
  const neg = value < 0;
  return (
    <div className={`flex justify-between py-1 border-b border-border/40 ${bold ? "font-semibold" : ""} ${accent ? "text-primary text-base" : ""}`}>
      <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
      <span className={neg ? "text-destructive" : ""}>{neg ? "−" : ""}{money(Math.abs(value))}</span>
    </div>
  );
}

export default function Analytics() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics?month=${month}`).then((r) => setData(r.data));
  }, [month]);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const t = data.totals;
  const ow = data.owner;
  const discountData = data.discount_distribution.filter((d) => d.level <= 5 || d.bags > 0).map((d) => ({ name: LEVEL_LABELS[d.level] || `${d.level}%`, bags: d.bags }));

  return (
    <div>
      <PageHeader
        title="Analytics & Reporting"
        subtitle={`Collected sales · multi-currency rollup to AED · ${month}${data.month_closed ? " · month closed" : ""}`}
        actions={<Input type="month" className="w-40 h-10" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="analytics-month-input" />}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Collected Sales" value={money(t.total_sales)} />
        {ow ? <StatCard label="Owner Net Profit" value={money(ow.net_profit)} sub={`${ow.owner_pct_of_profit}% of gross profit kept`} />
          : <StatCard label="Scope" value="My team" sub="collected sales of your agents" />}
        <StatCard label="Commission cost" value={`${t.commission_pct_of_sales}%`} sub="of sales" accent />
        <StatCard label="Deposit coverage" value={`${data.credit_risk.coverage_pct}%`} sub={`vs credit out · ${data.credit_risk.open_deposit_adjustments} open adjustment(s)`} accent />
      </div>

      {ow && <Card className="p-5 mt-4" data-testid="owner-bottom-line">
        <h3 className="font-display font-semibold mb-1">Owner bottom line · {month}</h3>
        <p className="text-xs text-muted-foreground mb-4">Gross profit is ex-VAT: (sale price − cost) ÷ (1 + VAT). Bonuses and the above-list split are settled separately, exactly as in the commission workbook.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1.5 text-sm">
          <Row label="Gross profit on collected bags (ex-VAT)" value={ow.gross_profit_ex_vat} />
          <Row label="Less: agent base commission" value={-ow.base_commission} />
          <Row label="Owner profit after agent commission" value={ow.profit_after_commission} bold />
          <Row label="Less: accelerator bonuses" value={-ow.accelerator_bonus} />
          <Row label="Less: new-customer bonuses" value={-ow.new_customer_bonus} />
          <Row label="Less: Star Agent bonus" value={-ow.star_bonus} />
          <Row label={`Plus: owner share of above-list premium (of ${money(ow.above_premium_collected)} collected)`} value={ow.owner_share_premium} />
          <Row label="OWNER NET PROFIT (after all agent pay)" value={ow.net_profit} bold accent />
        </div>
      </Card>}

      {ow && <Card className="p-5 mt-4" data-testid="exports-card">
        <h3 className="font-display font-semibold mb-1">Owner exports (CSV / Excel)</h3>
        <p className="text-xs text-muted-foreground mb-3">Invoices and payouts are filtered to {month}; the others export everything.</p>
        <div className="flex flex-wrap gap-2">
          {[["invoices", `invoices-${month}.csv`, true], ["payouts", `payouts-${month}.csv`, true], ["agents", "agents.csv"], ["customers", "customers.csv"], ["deposit-ledger", "deposit-ledger.csv"], ["stock-ledger", "stock-ledger.csv"]].map(([k, f, m]) => (
            <Button key={k} size="sm" variant="outline" onClick={() => downloadCsv(`/admin/export/${k}${m ? `?month=${month}` : ""}`, f)} data-testid={`export-${k}`}>
              <Download className="w-3.5 h-3.5 mr-1" /> {k.replace("-", " ")}
            </Button>
          ))}
        </div>
      </Card>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Profit by product · ex-VAT after commission (AED)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.product_mix} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="product" tick={{ fontSize: 9 }} width={120} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Bar dataKey="profit" radius={[0, 6, 6, 0]} fill="hsl(var(--chart-1))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Discount-level distribution (bags)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={discountData} dataKey="bags" nameKey="name" outerRadius={90} label>
                  {discountData.map((_, i) => <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Country-wise sales (AED)</h3>
          <div className="space-y-3">
            {data.country_sales.map((c) => {
              const max = Math.max(...data.country_sales.map((x) => x.sales));
              return (
                <div key={c.country}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{c.country}</span><span className="font-semibold">{money(c.sales)}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-accent rounded-full" style={{ width: `${(c.sales / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Per-agent contribution</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2">Agent</th>
                  <th className="text-right font-medium py-2">Sales</th>
                  <th className="text-right font-medium py-2">Earnings</th>
                  <th className="text-right font-medium py-2">Tier</th>
                </tr>
              </thead>
              <tbody>
                {data.per_agent.map((a) => (
                  <tr key={a.agent_id} className="border-b border-border/40">
                    <td className="py-2">{a.name}</td>
                    <td className="py-2 text-right">{money(a.collected_sales)}</td>
                    <td className="py-2 text-right">{money(a.total_earnings)}</td>
                    <td className="py-2 text-right">{["Base", "+10%", "+20%", "+30%"][a.accelerator_tier]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
